/**
 * JobProcessExpander — "what process made this image": a lazy expander that
 * fetches one studio async-generation job's trace (getAssetGenTrace,
 * operator-tiered server-side) and renders the pipeline phases + the shared
 * per-asset chain detail (AssetProcessDetail — the same renderer the build
 * trace explorer's drill-in uses). Mounted per JOB in the asset studio
 * (variant grids + failed-job rows).
 */
import { useState } from 'react';
import type { AssetGenTraceView } from '../services/types.js';
import { useServices } from '../services/context.js';
import { AssetProcessDetail } from './AssetProcessDetail.js';
import { assetChain, assetNames, assetPhases, type TraceStep } from './traceModel.js';

const fmtDur = (ms: number): string => (ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`);

export function JobProcessExpander({ creationId, jobId }: { creationId: string; jobId: string }) {
  const services = useServices();
  const [trace, setTrace] = useState<AssetGenTraceView | null>(null);
  const [redacted, setRedacted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const load = (): void => {
    if (trace || loading) return;
    setLoading(true);
    setError('');
    services.callable
      .getAssetGenTrace({ creationId, jobId })
      .then((res) => {
        setTrace(res.trace);
        setRedacted(res.redacted);
      })
      .catch((e) => setError((e as Error).message))
      .finally(() => setLoading(false));
  };

  const steps = (trace?.steps ?? []) as TraceStep[];
  const phases = assetPhases(steps);
  const name = assetNames(steps)[0];

  return (
    <details
      data-testid={`asset-process-${jobId}`}
      style={{ margin: '4px 0 0' }}
      onToggle={(e) => {
        if ((e.target as HTMLDetailsElement).open) load();
      }}
    >
      <summary className="muted" style={{ cursor: 'pointer', fontSize: 12 }}>
        View process
      </summary>
      {loading && (
        <p className="muted" style={{ fontSize: 12 }}>
          Loading process trace…
        </p>
      )}
      {error && (
        <p className="error-text" style={{ fontSize: 12 }}>
          {error}
        </p>
      )}
      {trace && (
        <div style={{ padding: '6px 0 0 6px' }}>
          {phases.length > 0 && (
            <p className="muted" style={{ fontSize: 11.5, margin: '0 0 4px' }}>
              {phases
                .map(
                  (p) =>
                    `${String(p.phase)} ${
                      typeof p.durationMs === 'number' ? fmtDur(p.durationMs) : ''
                    } ${p.ok === false ? '✗' : '✓'}`,
                )
                .join(' · ')}
            </p>
          )}
          {name ? (
            <AssetProcessDetail
              chain={assetChain(steps, name)}
              startedAt={trace.startedAt}
              redacted={redacted}
            />
          ) : (
            <p className="muted" style={{ fontSize: 12 }}>
              No per-asset steps recorded.
            </p>
          )}
        </div>
      )}
    </details>
  );
}
